import {useMutation, UseMutationResult, useQueryClient} from '@tanstack/react-query';

import {Locale} from '../../../../provider/locale/locale';
import {useSnackbar} from '../../../../provider/snackbar/snackbar-hook';
import {postBulkReviewNotes} from '../../../../service/reviews/reviews';
import {getReviewNotesQueryKey, ReviewNoteResponseType} from '../../../../service/reviews/reviews-notes';
import {BulkReviewNotesResultType, ReviewDataType} from '../../../../service/reviews/reviews-type';

type BulkReviewNotesApplyMutationVariablesType = {
    reviewIds: Array<number>;
    text: string;
};

type UseBulkReviewNotesApplyMutationOptionsType = {
    refreshReviewsInfo: () => Promise<unknown>;
    updateReview: (reviewId: number, updateData: Partial<ReviewDataType>) => void;
    onFlowComplete?: () => void;
};

export function useBulkReviewNotesApplyMutation(
    options: UseBulkReviewNotesApplyMutationOptionsType
): UseMutationResult<BulkReviewNotesResultType, unknown, BulkReviewNotesApplyMutationVariablesType> {
    const {refreshReviewsInfo, updateReview, onFlowComplete} = options;
    const {snackbar} = useSnackbar();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({reviewIds, text}: BulkReviewNotesApplyMutationVariablesType) =>
            postBulkReviewNotes({reviewIds, text}),
        onError: () => {
            snackbar.error(<Locale stringKey="REVIEWS__BULK_NOTE__ERROR" />);
        },
        onSuccess: async (result, variables) => {
            await refreshReviewsInfo();

            const canMapResultsByIndex = result.results.length === variables.reviewIds.length;

            if (canMapResultsByIndex) {
                variables.reviewIds.forEach((reviewId, index) => {
                    const note = result.results[index];

                    if (!note) {
                        return;
                    }

                    const existingNotes =
                        queryClient.getQueryData<ReviewNoteResponseType>(getReviewNotesQueryKey(reviewId))?.results ??
                        [];

                    updateReview(reviewId, {notes: [note, ...existingNotes]});
                });
            } else {
                await Promise.all(
                    variables.reviewIds.map((reviewId) =>
                        queryClient.invalidateQueries({queryKey: getReviewNotesQueryKey(reviewId)})
                    )
                );
            }

            const processedCount = result.results.length;

            snackbar.success({
                message: <Locale stringKey="REVIEWS__BULK_NOTE__SUCCESS_TITLE" />,
                description: (
                    <Locale
                        stringKey="REVIEWS__BULK_NOTE__SUCCESS_DESCRIPTION"
                        valueMap={{processedCount: String(processedCount)}}
                    />
                ),
            });

            onFlowComplete?.();
        },
    });
}
